import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  ArchiveBoxIcon,
  MultiSelect,
  Option,
  Sector,
  SectorIcon,
  Site,
  SiteIcon,
  ThemeIcon,
  UserUmanforge,
  WeekDays,
  WeekDaysButtons,
  CheckIcon,
  CrossIcon,
  RoundToggle
} from '@umanforge/common';

import InputText from '@common_components/inputs/textInput.component';
import SelectInput from '@common_components/inputs/selects/selectInput.component';
import Button from '@common_components/buttons/button.component';
import { UserIcon } from '@common_components/icons/user.icon';
import { CalendarIcon } from '@common_components/icons/calendar.icon';
import { TitleIcon } from '@common_components/icons/title.icon';
import { BacklogIcon } from '@common_components/icons/backlog.icon';

import { MAX_LENGTH_ENTITY } from 'constants/limite.constants';
import { DEFAULT_BACKLOG } from 'constants/eventManager.constant';
import { Theme } from 'interfaces/theme.interface';

import { PlusIcon } from '@common_components/icons/plus.icon';

interface EventManagerMainSettingsProps {
  eventManagerName: string;
  setEventManagerName: ( value: string ) => void;

  sitesOption: Option<Site>[];
  selectedSites: Option<Site>[];
  setSelectedSites: ( value: Option<Site>[] ) => void;

  sectorsOption: { label: string; options: Option<Sector>[] }[];
  selectedSectors: Option<Sector>[];
  setSelectedSectors: ( value: Option<Sector>[] ) => void;

  adminsOption: Option<UserUmanforge>[];
  selectedAdmins: Option<UserUmanforge>[];
  setSelectedAdmins: ( value: Option<UserUmanforge>[] ) => void;

  participantsOption: Option<UserUmanforge>[];
  selectedParticipants: Option<UserUmanforge>[];
  handleParticipantIdsChange: ( value: Option<UserUmanforge>[] ) => void;

  themesOption: Option<Theme>[];
  selectedThemes: Option<Theme>[];
  setSelectedThemes: ( value: Option<Theme>[] ) => void;

  navbarPosition: string;
  setNavbarPosition: ( value: string ) => void;

  numberOfVisibleDays: string;
  setNumberOfVisibleDays: ( value: string ) => void;

  daysBeforeArchived: string;
  setDaysBeforeArchived: ( value: string ) => void;

  visibleDays: WeekDays;
  setVisibleDays: ( value: WeekDays ) => void;

  activeField: boolean;
  setActiveField: ( value: boolean ) => void;

  backlogName: string;
  setBacklogName: ( value: string ) => void;

  usersCanCreateFromBoard: boolean;
  isAdminOfSelectedSectors: boolean;

  isUserCreationView: boolean;
  setIsUserCreationView: ( value: boolean ) => void;
  newUserFirstName: string;
  setNewUserFirstName: ( value: string ) => void;
  newUserLastName: string;
  setNewUserLastName: ( value: string ) => void;
  newUserEmail: string;
  setNewUserEmail: ( value: string ) => void;
  onCreateUser: () => Promise<void> | void;
  isUserCreationFormValid: boolean;
}

const EventManagerMainSettings: React.FC<EventManagerMainSettingsProps> = props => {
  const { t } = useTranslation();
  const {
    eventManagerName,
    setEventManagerName,
    sitesOption,
    selectedSites,
    setSelectedSites,
    sectorsOption,
    selectedSectors,
    setSelectedSectors,
    adminsOption,
    selectedAdmins,
    setSelectedAdmins,
    participantsOption,
    selectedParticipants,
    handleParticipantIdsChange,
    themesOption,
    selectedThemes,
    setSelectedThemes,
    navbarPosition,
    setNavbarPosition,
    numberOfVisibleDays,
    setNumberOfVisibleDays,
    daysBeforeArchived,
    setDaysBeforeArchived,
    visibleDays,
    setVisibleDays,
    activeField,
    setActiveField,
    backlogName,
    setBacklogName,
    usersCanCreateFromBoard,
    isAdminOfSelectedSectors,
    isUserCreationView,
    setIsUserCreationView,
    newUserFirstName,
    setNewUserFirstName,
    newUserLastName,
    setNewUserLastName,
    newUserEmail,
    setNewUserEmail,
    onCreateUser,
    isUserCreationFormValid
  } = props;

  const fetchNavbarOptions = (): Promise<{ options: { id: string; label: string }[] }[]> => {
    return Promise.resolve( [
      {
        options: [
          { id: 'auto', label: t( 'common:auto' ) },
          { id: 'left', label: t( 'common:left' ) },
          { id: 'top', label: t( 'common:top' ) },
        ],
      }
    ] );
  };

  return (
    <>
      <InputText
        label={ t( 'common:name' ) }
        icon={ <TitleIcon className='text-gray-base-dark w-7 h-7' /> }
        value={ eventManagerName }
        onChange={ setEventManagerName }
        labelPosition='top'
        maxCharacter={ MAX_LENGTH_ENTITY.EVENT_MANAGER_NAME }
        isRequired
      />
      <MultiSelect
        label={ t( 'common:sites' ) }
        icon={ <SiteIcon className='w-7 h-7 text-gray-base-dark' /> }
        getValues={ sitesOption }
        selectedValues={ selectedSites }
        onChange={ setSelectedSites }
        required
      />
      <MultiSelect
        label={ t( 'common:sectors' ) }
        icon={ <SectorIcon className='w-7 h-7 text-gray-base-dark' /> }
        getValues={ sectorsOption }
        selectedValues={ selectedSectors }
        onChange={ setSelectedSectors }
        required
      />
      <MultiSelect
        label={ t( 'common:administrators' ) }
        icon={ <UserIcon className='text-gray-base-dark w-7 h-7' /> }
        getValues={ adminsOption }
        selectedValues={ selectedAdmins }
        onChange={ setSelectedAdmins }
        required
      />
      <MultiSelect
        label={ t( 'common:participants' ) }
        icon={ <UserIcon className='text-gray-base-dark w-7 h-7' /> }
        getValues={ participantsOption }
        selectedValues={ selectedParticipants }
        onChange={ handleParticipantIdsChange }
        required
      />
      {usersCanCreateFromBoard && isAdminOfSelectedSectors && (
        <div className="mt-2">
          {isUserCreationView ? (
            <div className="p-3 border border-gray-200 rounded-md bg-gray-50">
              <h4 className="text-sm font-medium mb-2">{t( 'translation:create_user' )}</h4>
              <InputText
                label={ t( 'translation:Firstname' ) }
                value={ newUserFirstName }
                onChange={ setNewUserFirstName }
                labelPosition='top'
                maxCharacter={ MAX_LENGTH_ENTITY.UF_USER_FIRSTNAME }
              />
              <InputText
                label={ t( 'translation:Lastname' ) }
                value={ newUserLastName }
                onChange={ setNewUserLastName }
                labelPosition='top'
                maxCharacter={ MAX_LENGTH_ENTITY.UF_USER_LASTNAME }
              />
              <InputText
                label={ t( 'translation:email' ) }
                value={ newUserEmail }
                onChange={ setNewUserEmail }
                labelPosition='top'
                maxCharacter={ MAX_LENGTH_ENTITY.UF_USER_EMAIL }
                type='text'
              />
              <div className="flex justify-end gap-2 mt-2">
                <Button
                  label={t( 'common:cancel' )}
                  icon={<CrossIcon className='w-5 h-5' />}
                  onClick={() => { setIsUserCreationView( false ); }}
                  padding='py-3 px-5'
                  textColor='text-gray-600'
                  color='bg-gray-100'
                  colorHover='hover:bg-gray-200'
                  hoverBorderColor='hover:border-transparent'
                  textSize='text-sm'
                  textColorHover='hover:text-gray-600'
                />
                <Button
                  label={t( 'common:create' )}
                  icon={<CheckIcon className='w-5 h-5' />}
                  padding='py-3 px-5'
                  textSize='text-sm'
                  textColor='text-green-700'
                  textColorHover='hover:text-green-700'
                  hoverBorderColor='hover:border-transparent'
                  color='bg-green-200'
                  shadow='shadow-none'
                  colorHover='hover:bg-green-300'
                  disable={!isUserCreationFormValid}
                  onClick={onCreateUser}
                />
              </div>
            </div>
          ) : (
            <div>
              <Button
                icon={ <PlusIcon className='group-hover:rotate-[360deg] transition-transform duration-500 w-5 h-5' /> }
                label={t( 'translation:create_user' ) }
                onClick={() => { setIsUserCreationView( true ); }}
                padding='py-3 px-5'
                textColor='text-green-700'
                textColorHover='hover:text-green-700'
                hoverBorderColor='hover:border-transparent'
                color='bg-green-200'
                shadow='shadow-none'
                colorHover='hover:bg-green-300'
                textSize='text-sm'
              />
            </div>
          )}
        </div>
      )}
      <MultiSelect
        label={ t( 'common:themes' ) }
        icon={ <ThemeIcon className='h-7 w-7 text-gray-base-dark' /> }
        getValues={ themesOption }
        selectedValues={ selectedThemes }
        onChange={ setSelectedThemes }
      />
      <SelectInput
        icon={ <ArchiveBoxIcon className='h-7 w-7 text-gray-base-dark' /> }
        label={ t( 'common:navbar' ) }
        fetchOptions={ fetchNavbarOptions }
        labelPosition='top'
        selected={ navbarPosition }
        onSelect={ setNavbarPosition }
        isRequired
      />
      <div className='flex gap-4'>
        <InputText
          key={ numberOfVisibleDays }
          labelPosition='top'
          icon={ <CalendarIcon className='text-gray-base-dark w-7 h-7' /> }
          label={ t( 'translation:number_of_day_to_show' ) }
          tooltip={ t( 'translation:number_of_day_visible_in_the_calendar_view' ) }
          value={ numberOfVisibleDays }
          onChange={ setNumberOfVisibleDays }
          type="number"
          min={ 1 }
          max={ 7 }
          isRequired
        />
        <InputText
          key={ daysBeforeArchived }
          icon={ <CalendarIcon className='text-gray-base-dark w-7 h-7' /> }
          label={ t( 'translation:day_before_archived' ) }
          tooltip={ t( 'translation:number_of_day_before_event_completed_are_archived' ) }
          type="number"
          min={ 0 }
          labelPosition='top'
          max={ 3 }
          value={ daysBeforeArchived }
          onChange={ setDaysBeforeArchived }
          isRequired
        />
      </div>
      <WeekDaysButtons
        labelPosition='top'
        label={ t( 'translation:visible_days' ) }
        onSelectionChange={ setVisibleDays }
        defaultSelectedDays={ visibleDays }
      />
      <RoundToggle
        className='pt-2'
        label={ t( 'translation:display_fileds_setting' ) }
        checked={ activeField }
        onChange={ setActiveField }
        checkedButtonClass='bg-orange-base-light hover:bg-orange-base-light'
        checkedIconClass='text-white'
      />
      <InputText
        labelPosition='top'
        icon={ <BacklogIcon className='w-10 h-10' /> }
        label={ t( 'translation:backlog_name' ) }
        value={ backlogName == DEFAULT_BACKLOG.NAME ? t( backlogName ) : backlogName }
        onChange={ setBacklogName }
        isRequired
      />
    </>
  );
};

export default EventManagerMainSettings;
